import { cn } from '@plato/ui/util/classes';
import { useState } from 'react';
import { Input } from '../ui/input.tsx';
import type { NumberRangeFilterOption } from './types/filter-types.ts';

type NumberRangeFilterProps = {
    filter: NumberRangeFilterOption;
    onChange: (value: { min?: number; max?: number } | null) => void;
    className?: string;
};

export function NumberRangeFilter({ filter, onChange, className }: NumberRangeFilterProps) {
    const [minValue, setMinValue] = useState<string>(filter.selectedRange?.min?.toString() || '');
    const [maxValue, setMaxValue] = useState<string>(filter.selectedRange?.max?.toString() || '');

    const handleMinChange = (value: string) => {
        setMinValue(value);
        const numericValue = value === '' ? undefined : Number(value);

        if (value === '' || (numericValue !== undefined && !Number.isNaN(numericValue))) {
            onChange({
                min: numericValue,
                max: filter.selectedRange?.max,
            });
        }
    };

    const handleMaxChange = (value: string) => {
        setMaxValue(value);
        const numericValue = value === '' ? undefined : Number(value);

        if (value === '' || (numericValue !== undefined && !Number.isNaN(numericValue))) {
            onChange({
                min: filter.selectedRange?.min,
                max: numericValue,
            });
        }
    };

    return (
        <div className={cn('flex flex-col space-y-3', className)}>
            <div className="flex items-center space-x-2">
                <div className="flex-1">
                    <div className="text-xs text-neutral-500 mb-1">
                        Min {filter.unit ? `(${filter.unit})` : ''}
                    </div>
                    <Input
                        type="number"
                        placeholder={filter.minValue?.toString() || '0'}
                        value={minValue}
                        onChange={(e) => {
                            handleMinChange(e.target.value);
                        }}
                        min={filter.minValue}
                        max={filter.maxValue}
                    />
                </div>
                <div className="text-neutral-400 px-2 pt-5">-</div>
                <div className="flex-1">
                    <div className="text-xs text-neutral-500 mb-1">
                        Max {filter.unit ? `(${filter.unit})` : ''}
                    </div>
                    <Input
                        type="number"
                        placeholder={filter.maxValue?.toString() || '∞'}
                        value={maxValue}
                        onChange={(e) => {
                            handleMaxChange(e.target.value);
                        }}
                        min={filter.minValue}
                        max={filter.maxValue}
                    />
                </div>
            </div>

            {(filter.minValue !== undefined || filter.maxValue !== undefined) && (
                <div className="text-xs text-neutral-500">
                    {filter.minValue !== undefined &&
                        filter.maxValue !== undefined &&
                        `Range: ${filter.minValue} - ${filter.maxValue}${filter.unit ? ` ${filter.unit}` : ''}`}
                    {filter.minValue !== undefined &&
                        filter.maxValue === undefined &&
                        `Min: ${filter.minValue}${filter.unit ? ` ${filter.unit}` : ''}`}
                    {filter.minValue === undefined &&
                        filter.maxValue !== undefined &&
                        `Max: ${filter.maxValue}${filter.unit ? ` ${filter.unit}` : ''}`}
                </div>
            )}
        </div>
    );
}
